import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {
  formatErrors,
  msgTypeMessage,
  aiOriginApproved,
  aiReviewApproved,
  aiReviewDeclined,
  aiDeclineAutoComment,
  aiConfidenceLevel,
  aiConfidencePercent,
  aiConfidenceLabel,
  UPDATE_CHAT_AI_CARD_EDIT,
  UPDATE_CHAT_AI_BATCH,
  UPDATE_CHAT_AI_BATCH_CLEAR,
  UPDATE_CHAT_REPLY_MESSAGE
} from '../../../constants';
import {chatMessageAddAction} from '../../../actions/chatAction';
import {aiCardReviewAction} from '../../../actions/clientAction';
import {ChatMessageText} from './ChatMessage';

// AI answer card (Design3): shows the confidence and offers apply/edit/decline.
export function ChatAiCard({clientId, chatID}) {
  const dispatch = useDispatch();
  const aiBatch = useSelector(state => state.chat.aiBatch);
  const aiCardEdit = useSelector(state => state.chat.aiCardEdit);
  const replied = useSelector(state => state.chat.replied);
  const [loading, setLoading] = useState(false);

  // while edit mode is on the text lives in the input, so the card itself is not duplicated
  if (!aiBatch || !aiBatch.generated_response || aiCardEdit) {
    return null;
  }

  const level = aiConfidenceLevel(aiBatch);
  const percent = aiConfidencePercent(aiBatch);
  const text = aiBatch.generated_response;

  // Drop the Z like the rest of the feed does (ChatMessageTime): chat times are Moscow time,
  // otherwise the card shows an hour offset ahead of the messages next to it.
  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(String(ts).replace('Z', ''));
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return pad(d.getHours()) + ':' + pad(d.getMinutes());
  };

  // empty final_text/comment are stripped by aiCardReviewAction itself
  const reportReview = (status, finalText, comment) => {
    return dispatch(aiCardReviewAction({
      client_id: clientId,
      batch_end: aiBatch.batch_end,
      status,
      final_text: finalText,
      comment
    }));
  };

  const handleApply = () => {
    if (loading) return;
    setLoading(true);
    // Dismiss the card before the server answers: while the send is in flight the input panel
    // stays unlocked, so a second answer from the curator would follow the first to the client
    // and gamma would get a rejection for an already approved batch. The reply chip is cleared
    // too: the card is sent without a quote link, and a stale chip would thread the next
    // message to the wrong target. The suggestion is restored if the send fails.
    const batch = aiBatch;
    const repliedBefore = replied;
    dispatch({type: UPDATE_CHAT_AI_BATCH_CLEAR, client_id: clientId});
    if (repliedBefore) {
      dispatch({type: UPDATE_CHAT_REPLY_MESSAGE, message: null});
    }
    dispatch(chatMessageAddAction(clientId, chatID, msgTypeMessage, text, null, null, null, false, aiOriginApproved))
      .then(() => {
        setLoading(false);
        reportReview(aiReviewApproved, text).then((res) => {
          if (!res.success) {
            toast.warn('Ответ отправлен, но оценка AI не записана', {position: toast.POSITION.BOTTOM_RIGHT});
          }
        });
      })
      .catch((err) => {
        dispatch({type: UPDATE_CHAT_AI_BATCH, data: batch, client_id: clientId});
        if (repliedBefore) {
          dispatch({type: UPDATE_CHAT_REPLY_MESSAGE, message: repliedBefore});
        }
        setLoading(false);
        toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
      });
  };

  // Decline dismisses the card up front, same as apply: while the request is in flight the input
  // panel stays unlocked, and an answer sent from there would post a second decline for the same
  // batch. Nothing reached the client, so on failure the suggestion comes back and can be retried.
  const handleDecline = () => {
    if (loading) return;
    setLoading(true);
    const batch = aiBatch;
    dispatch({type: UPDATE_CHAT_AI_BATCH_CLEAR, client_id: clientId});
    reportReview(aiReviewDeclined, null, aiDeclineAutoComment).then((res) => {
      setLoading(false);
      if (!res.success) {
        dispatch({type: UPDATE_CHAT_AI_BATCH, data: batch, client_id: clientId});
        toast.error(res.error ? formatErrors([res.error]) : 'Не удалось отклонить AI-ответ', {position: toast.POSITION.BOTTOM_RIGHT});
      }
    });
  };

  const handleEdit = () => {
    dispatch({type: UPDATE_CHAT_AI_CARD_EDIT, data: {
      client_id: clientId,
      batch_end: aiBatch.batch_end,
      original_text: text
    }});
  };

  return (
    <div className={"ai-card ai-card--" + (level || 'pending')}>
      <div className="ai-card__head">
        <span className="ai-card__dot"></span>
        <span className="ai-card__title">AI-ответ</span>
        <span className="ai-card__badge">
          {aiConfidenceLabel(level)}{percent !== null ? ' · ' + percent + '%' : ''}
        </span>
        {aiBatch.generated_at && <span className="ai-card__time">{formatTime(aiBatch.generated_at)}</span>}
      </div>
      <div className="ai-card__text">
        <ChatMessageText message={text}/>
      </div>
      <div className="ai-card__actions">
        <button type="button" className="btn ai-card__btn ai-card__btn--apply" disabled={loading} onClick={handleApply}>Применить</button>
        <button type="button" className="btn ai-card__btn" disabled={loading} onClick={handleEdit}>Отредактировать</button>
        <button type="button" className="btn ai-card__btn ai-card__btn--decline" disabled={loading} onClick={handleDecline}>Отклонить</button>
      </div>
    </div>
  );
}
